import styled from '@emotion/styled';

import Box from '~/common/components/Box';
import Text from '~/common/components/Texts/Text';

interface StyledScaleProgressBarProps {
  percentage: number;
  isVisible: boolean;
}

interface StyledBOLTLogoProps {
  isComplete: boolean;
}

interface StyledSalesBlurbProps {
  isCarousel: boolean;
  isComplete: boolean;
}

export const StyledScaleProgressBar = styled(Box)<StyledScaleProgressBarProps>`
  width: ${({ percentage }) => `calc(428px * ${percentage})`};
  height: 10px;
  background: linear-gradient(
    to right,
    rgba(4, 37, 60, 1) 0%,
    rgba(4, 37, 60, 1) 0%,
    rgba(11, 100, 162, 1) 100%
  );
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: width 50ms linear;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: ${({ percentage }) => (percentage < 0.15 ? `100%` : `25%`)};
    height: 100%;
    background-image: radial-gradient(
      circle at center,
      rgba(89, 180, 244, 1) 0%,
      rgba(89, 180, 244, 0.94) 8%,
      rgba(89, 180, 244, 0.87) 17%,
      rgba(11, 100, 162, 0.67) 42%,
      rgba(4, 37, 60, 0.2) 100%
    );
    animation: ${({ percentage }) =>
      percentage < 0.15
        ? `slide 3000ms linear infinite`
        : `slide 2500ms linear infinite`};
    opacity: ${(props) =>
      props.isVisible ? 1 : 0}; /* Adjust overlay visibility */
    pointer-events: none; /* Ensure it doesn't interfere with interactions */
  }

  @keyframes slide {
    0% {
      transform: translateX(-50px);
    }
    100% {
      transform: ${({ percentage }) =>
        percentage < 0.15 ? `translateX(150px)` : `translateX(482px)`};
    }
  }
`;

function getBOLTLogoAnimation(isComplete: boolean): string {
  return isComplete ? 'pulseOpacity 3s infinite ease-in-out' : 'none';
}

function getBOLTLogoStyles(props: { isComplete: boolean }): string {
  return getBOLTLogoAnimation(props.isComplete);
}

export const StyledBOLTLogo = styled.img<StyledBOLTLogoProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 186px;
  height: 126px;
  margin-bottom: 50px;
  opacity: 1;
  animation: ${getBOLTLogoStyles};

  @keyframes pulseOpacity {
    0%,
    100% {
      opacity: 0.25;
    }
    50% {
      opacity: 1;
    }
  }
`;

export const StyledSalesBlurb = styled(Text)<StyledSalesBlurbProps>`
  font-size: 1.5rem;
  font-weight: 500;
  justify-content: center;
  margin-top: 1rem;
  position: absolute;
  opacity: ${(props) => (props.isCarousel ? 0 : 1)};
  transform: ${(props) =>
    props.isCarousel ? `translateY(-5px)` : `translateY(0)`};
  animation: ${(props) =>
    props.isCarousel
      ? (props.isComplete && `fadeOut 750ms ease-in-out`) ||
        `fadeInOut 5s ease-in-out infinite`
      : 'none'};

  @keyframes fadeOut {
    0% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(-5px);
    }
  }

  @keyframes fadeInOut {
    0% {
      opacity: 0;
      transform: translateY(-5px);
    }
    10% {
      opacity: 1;
      transform: translateY(0);
    }
    90% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(-5px);
    }
  }
`;

export const StyledLoadingStatus = styled(Box)<{ isFadingOut: boolean }>`
  display: flex;
  font-size: 0.875rem;
  font-weight: 500;
  opacity: ${({ isFadingOut }) => (isFadingOut ? 0 : 1)};
  transition: opacity 300ms ease-in-out;

  animation: ${({ isFadingOut }) =>
    isFadingOut ? `fadeOut 500ms ease-in-out` : `fadeIn 300ms ease-in-out`};

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;
